import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useAuth} from '../context/AuthContext';
import {feedbackService,prepareAttachment,MAX_ATTACHMENTS} from '../services/feedbackService';

const {FiMessageSquare,FiX,FiPaperclip,FiCheckCircle,FiSend}=FiIcons;

// Botón flotante de soporte y comentarios; `page` es la sección desde la que se abre.
export default function FeedbackButton({page}) {
  const {user,isGuest}=useAuth();
  const [isOpen,setIsOpen]=useState(false);
  const [subject,setSubject]=useState('');
  const [message,setMessage]=useState('');
  const [files,setFiles]=useState([]);
  const [error,setError]=useState('');
  const [sending,setSending]=useState(false);
  const [sent,setSent]=useState(false);

  const open=()=>{
    setSent(false);
    setError('');
    setIsOpen(true);
  };

  const close=()=>{
    setIsOpen(false);
    if (sent) {
      setSubject('');
      setMessage('');
      setFiles([]);
    }
  };

  const addFiles=async (event)=>{
    const selected=[...event.target.files];
    event.target.value='';
    setError('');
    if (files.length+selected.length>MAX_ATTACHMENTS) {
      setError(`Máximo ${MAX_ATTACHMENTS} archivos`);
      return;
    }
    try {
      setFiles([...files,...await Promise.all(selected.map(prepareAttachment))]);
    } catch (err) {
      setError(err.message);
    }
  };

  const submit=async (event)=>{
    event.preventDefault();
    setSending(true);
    setError('');
    try {
      await feedbackService.send({subject,message,page,attachments: files});
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (isGuest) return null;

  return (
    <>
      <button
        onClick={open}
        title="Soporte y comentarios"
        aria-label="Soporte y comentarios"
        className="fixed bottom-5 right-5 z-[70] w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 text-white shadow-2xl shadow-black/50 border border-white/20 flex items-center justify-center transition-all hover:scale-110 active:scale-95"
      >
        <SafeIcon icon={FiMessageSquare} className="w-6 h-6" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-zinc-950/90 backdrop-blur-md"
            onClick={close}
          >
            <motion.div
              initial={{scale: 0.95,opacity: 0,y: 30}}
              animate={{scale: 1,opacity: 1,y: 0}}
              exit={{scale: 0.95,opacity: 0,y: 30}}
              className="glass-modal w-full max-w-lg max-h-[90vh] rounded-[2rem] overflow-y-auto custom-scrollbar"
              onClick={(event)=> event.stopPropagation()}
            >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/5">
                <div>
                  <span className="text-red-500 font-black tracking-[0.3em] uppercase text-[9px] block">Soporte</span>
                  <h2 className="text-xl font-black uppercase tracking-tight text-white">Comentarios</h2>
                </div>
                <button onClick={close} aria-label="Cerrar" className="p-3 bg-white/5 hover:bg-red-600 rounded-xl text-white transition-all">
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {sent ? (
                <div className="p-10 text-center space-y-4">
                  <SafeIcon icon={FiCheckCircle} className="w-14 h-14 text-green-400 mx-auto" />
                  <h3 className="text-2xl font-black uppercase tracking-tight text-white">¡Gracias!</h3>
                  <p className="text-zinc-300 text-sm">Recibimos tu mensaje y lo revisaremos pronto.</p>
                  <button onClick={close} className="mt-4 px-8 py-3 bg-white/10 hover:bg-white/20 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white">
                    Cerrar
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="p-6 space-y-5">
                  <div>
                    <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-white mb-2">Enviado por</span>
                    <div className="profile-input !bg-white/5 text-white/80">{user.name} · {user.email}</div>
                  </div>

                  <label className="block">
                    <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-white mb-2">Asunto</span>
                    <input
                      required
                      maxLength={150}
                      value={subject}
                      onChange={(event)=> setSubject(event.target.value)}
                      className="profile-input"
                      placeholder="¿Sobre qué quieres escribirnos?"
                    />
                  </label>

                  <label className="block">
                    <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-white mb-2">Mensaje</span>
                    <textarea
                      required
                      rows={5}
                      maxLength={5000}
                      value={message}
                      onChange={(event)=> setMessage(event.target.value)}
                      className="profile-input resize-none"
                      placeholder="Cuéntanos qué pasó, qué necesitas o qué mejorarías"
                    />
                  </label>

                  <div>
                    <span className="block text-[10px] font-black uppercase tracking-[0.2em] text-white mb-2">
                      Archivos (opcional)
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {files.map((file,index)=> (
                        <span key={index} className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white/10 border border-white/10 text-xs font-bold text-white">
                          <span className="max-w-[10rem] truncate">{file.name}</span>
                          <button
                            type="button"
                            aria-label={`Quitar ${file.name}`}
                            onClick={()=> setFiles(files.filter((_,i)=> i!==index))}
                            className="p-1 rounded-full hover:bg-red-600"
                          >
                            <SafeIcon icon={FiX} className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {files.length<MAX_ATTACHMENTS && (
                        <label className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-white/30 hover:border-white text-xs font-bold text-white transition-colors">
                          <SafeIcon icon={FiPaperclip} /> Adjuntar
                          <input type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" onChange={addFiles} className="hidden" />
                        </label>
                      )}
                    </div>
                    <p className="text-zinc-400 text-[11px] mt-2">Hasta {MAX_ATTACHMENTS} archivos: imágenes o PDF de máximo 1 MB.</p>
                  </div>

                  {error && <p className="text-red-400 text-sm font-bold">{error}</p>}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white py-4 rounded-2xl font-black uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-2 transition-all"
                  >
                    {sending ? 'Enviando…' : <>Enviar <SafeIcon icon={FiSend} /></>}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
